import { useMemo } from "react";
import { Box, GlobalStyles } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { CellClassParams, ColDef } from "ag-grid-community";
import type { BarrelLayoutRow } from "../models/RollsCartridge";

const gridStyles = (
	<GlobalStyles
		styles={{
			".barrel-layout-grid .tonnage-over": {
				color: "#d32f2f !important",
				fontWeight: 700,
			},
		}}
	/>
);

interface BarrelLayoutGridProps {
	rows: BarrelLayoutRow[];
}

const tonnageFormatter = (value: number | null | undefined): string => {
	if (value == null) return "0.00";
	return value.toFixed(2);
};

// Dedupe by PassPosition — the SP may return one row per stand for
// multi-stand passes. The legacy view shows one row per position.
const dedupePasses = (rows: BarrelLayoutRow[]): BarrelLayoutRow[] => {
	const seen = new Map<number, BarrelLayoutRow>();
	for (const row of rows) {
		if (row.PassPosition == null) continue;
		const existing = seen.get(row.PassPosition);
		if (!existing) {
			seen.set(row.PassPosition, row);
			continue;
		}
		// Prefer row with higher RolledTonnage (more used)
		const existingRolled = existing.RolledTonnage ?? 0;
		const currentRolled = row.RolledTonnage ?? 0;
		if (currentRolled > existingRolled) {
			seen.set(row.PassPosition, row);
		}
	}
	return [...seen.values()].sort(
		(a, b) => (a.PassPosition ?? 0) - (b.PassPosition ?? 0),
	);
};

const buildColumnDefs = (): ColDef<BarrelLayoutRow>[] => [
	{
		field: "PassPosition",
		headerName: "Number",
		width: 80,
		type: "rightAligned",
	},
	{
		field: "PassName",
		headerName: "Name",
		minWidth: 160,
		flex: 1,
	},
	{
		field: "PassTypeName",
		headerName: "Type",
		width: 100,
	},
	{
		field: "PassShapeName",
		headerName: "Shape",
		width: 120,
	},
	{
		headerName: "Sched/Rolled",
		width: 160,
		type: "rightAligned",
		valueGetter: (p) =>
			`${tonnageFormatter(p.data?.ScheduledTonnage)}/${tonnageFormatter(
				p.data?.RolledTonnage,
			)}`,
		cellClass: (p: CellClassParams<BarrelLayoutRow>) => {
			const sched = p.data?.ScheduledTonnage;
			const rolled = p.data?.RolledTonnage;
			if (sched != null && rolled != null && rolled > sched) {
				return "tonnage-over";
			}
			return "";
		},
	},
];

export const BarrelLayoutGrid = ({ rows }: BarrelLayoutGridProps) => {
	const columnDefs = useMemo(() => buildColumnDefs(), []);
	const dedupedRows = useMemo(() => dedupePasses(rows), [rows]);

	return (
		<Box className="barrel-layout-grid" sx={{ width: "100%" }}>
			{gridStyles}
			<AgGridReact<BarrelLayoutRow>
				columnDefs={columnDefs}
				rowData={dedupedRows}
				defaultColDef={{
					sortable: false,
					resizable: true,
				}}
				suppressContextMenu
				rowHeight={28}
				headerHeight={28}
				domLayout="autoHeight"
			/>
		</Box>
	);
};
